import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    Divider,
    IconButton,
    Tooltip,
    Alert,
} from "@mui/material";
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatListBulleted,
    FormatListNumbered,
    Link as LinkIcon,
    AutoAwesome,
    Code,
    Image as ImageIcon,
    CropLandscape,
    OpenInNew,
    Refresh,
    Fullscreen,
    ArrowUpward,
    ArrowForward,
    ArrowBack,
    Close,
    Save as SaveIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { generateEmailTemplateWithGemini } from "../../utils/geminiEmailTemplate";
import { getDummyTemplateById } from "../../utils/dummyEmailTemplatesStore";
import {
    captureContentEditableCaret,
    captureTextInputCaret,
    buildTemplateApiParamFields,
    extractEmailMergeVariables,
    insertTextAtStringCaret,
    removeEmailMergeParameterFromText,
    renameEmailMergeParameterInText,
    validateEmailMergeParameters,
    getPlainEmailHtmlForPreview,
    normalizePlainEmailHtml,
    EMAIL_MERGE_TAG_EDITOR_SX,
    decorateEmailMergeTagsForPreview,
    buildEmailPreviewDocumentHtml,
} from "../../utils/emailMergeVariables";
import useEmailMergeEditor from "../../hooks/useEmailMergeEditor";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import CustomizedSteppers from "./CustomizedSteppers";
import EmailMergeVariablesBar from "./EmailMergeVariablesBar";
import EmailMergeParameterNameDialog from "./EmailMergeParameterNameDialog";
import RawHtmlCodeEditor from "./RawHtmlCodeEditor";
import { AiPreviewCraftingLoader, AiChatCraftingCard } from "./AiCraftingLoader";
import { handleSimpleEditorPaste } from "../../utils/simpleEditorPaste";
import { normalizeEmailHtmlForEditor } from "../../utils/normalizeEmailHtml";
import { formatEmailHtmlSource } from "../../utils/formatEmailHtmlSource";
import base_url from "../../constants";
import useNotification from "../common/Notification";

const GLASS_PAPER_SX = {
    background: "rgba(255, 255, 255, 0.6)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.85)",
    borderRadius: 2,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
};

const fieldSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 1,
        backgroundColor: "#fff",
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.main",
            borderWidth: 2,
        },
    },
};

const EMPTY_CONTENT = {
    raw: "",
    simple: "",
};

const MIN_EDITOR_WIDTH = 25;
const MAX_EDITOR_WIDTH = 75;
const SPLITTER_WIDTH = 6;
const WIZARD_STEPS = ["Design Template", "Template Name", "Preview"];

const CreateNewTemplate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const notify = useNotification();
    const userId = Cookies.get("userId");
    const userRole = Cookies.get("userRole")?.split(",")[0];
    const [mode, setMode] = useState("simple");
    const [activeTab, setActiveTab] = useState("simple");
    const [contentByMode, setContentByMode] = useState(EMPTY_CONTENT);
    const [editorWidthPercent, setEditorWidthPercent] = useState(50);
    const [aiPrompt, setAiPrompt] = useState("");
    const [aiContext, setAiContext] = useState([]);
    const [aiBuiltHtml, setAiBuiltHtml] = useState("");
    const [aiViewMode, setAiViewMode] = useState("preview");
    const [aiIsBuilding, setAiIsBuilding] = useState(false);
    const [aiError, setAiError] = useState("");
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [templateName, setTemplateName] = useState("");
    const [wizardStep, setWizardStep] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [renameParameter, setRenameParameter] = useState(null);
    const simpleEditorRef = useRef(null);
    const rawEditorRef = useRef(null);
    const aiTextareaRef = useRef(null);
    const caretSnapshotRef = useRef(null);
    const splitContainerRef = useRef(null);
    const isDraggingRef = useRef(false);

    const content = useMemo(() => contentByMode[mode] || "", [contentByMode, mode]);
    const previewContent = useMemo(() => {
        if (activeTab === "ai") return aiBuiltHtml;
        if (mode === "simple") {
            return getPlainEmailHtmlForPreview(contentByMode.simple || "");
        }
        return content;
    }, [activeTab, mode, aiBuiltHtml, content, contentByMode.simple]);

    const highlightedPreviewHtml = useMemo(
        () => decorateEmailMergeTagsForPreview(previewContent),
        [previewContent]
    );

    const setSimpleHtml = useCallback((plain) => {
        setContentByMode((prev) => ({ ...prev, simple: plain }));
    }, []);

    const setRawHtml = useCallback((plain) => {
        setContentByMode((prev) => ({ ...prev, raw: plain }));
    }, []);

    const {
        readPlainFromEditor: readPlainFromSimpleEditor,
        applyPlainHtmlToEditor: applyPlainHtmlToSimpleEditor,
        refreshEditorDecoration: refreshSimpleEditorDecoration,
        runEditorCommand: runSimpleEditorCommand,
        handleEditorInput: handleSimpleInput,
        handleEditorBlur: handleSimpleBlur,
        handleEditorMouseDown: handleSimpleEditorMouseDown,
        handleEditorClick: handleSimpleEditorClick,
        handleEditorCopy: handleSimpleEditorCopy,
        handleEditorCut: handleSimpleEditorCut,
        handleEditorContextMenu: handleSimpleEditorContextMenu,
        insertMergeTagInEditor: insertMergeTagInSimpleEditor,
    } = useEmailMergeEditor({
        editorRef: simpleEditorRef,
        html: contentByMode.simple,
        setHtml: setSimpleHtml,
        enabled: activeTab === "simple" && mode === "simple",
        onMergeTagRenameRequest: (oldName) => setRenameParameter(oldName),
    });

    const applyPlainHtmlToRawEditor = useCallback(
        (plain, { format = true } = {}) => {
            const next = format
                ? formatEmailHtmlSource(plain)
                : String(plain || "");
            setRawHtml(next);
            rawEditorRef.current?.setValue?.(next, { format: false });
        },
        [setRawHtml]
    );

    const readPlainFromRawEditor = useCallback(() => {
        if (rawEditorRef.current?.getValue) {
            return rawEditorRef.current.getValue();
        }
        return contentByMode.raw ?? "";
    }, [contentByMode.raw]);

    const getActiveHtml = useCallback(() => {
        if (activeTab === "ai") {
            return aiBuiltHtml;
        }
        if (mode === "simple") {
            if (simpleEditorRef.current) {
                return readPlainFromSimpleEditor();
            }
            return contentByMode.simple ?? "";
        }
        return readPlainFromRawEditor();
    }, [
        activeTab,
        mode,
        aiBuiltHtml,
        contentByMode,
        readPlainFromSimpleEditor,
        readPlainFromRawEditor,
    ]);

    const detectedVariables = useMemo(
        () => extractEmailMergeVariables(previewContent),
        [previewContent]
    );

    const mergeParameterError = useMemo(
        () =>
            validateEmailMergeParameters(
                previewContent,
                contentByMode.simple,
                contentByMode.raw,
                aiBuiltHtml
            ),
        [previewContent, contentByMode.simple, contentByMode.raw, aiBuiltHtml]
    );

    const captureEditorCaret = useCallback(() => {
        if (activeTab === "ai") {
            caretSnapshotRef.current = captureTextInputCaret(aiTextareaRef.current);
            return;
        }

        if (mode === "simple") {
            caretSnapshotRef.current = captureContentEditableCaret(simpleEditorRef.current);
            return;
        }

        caretSnapshotRef.current = rawEditorRef.current?.getCaret?.() ?? null;
    }, [activeTab, mode]);

    const insertMergeVariable = useCallback(
        (name) => {
            if (!name) return;
            const tag = `{{${name}}}`;
            const caret = caretSnapshotRef.current;

            if (activeTab === "ai") {
                const el = aiTextareaRef.current;
                const { value, cursor } = insertTextAtStringCaret(
                    aiBuiltHtml,
                    tag,
                    caret || captureTextInputCaret(el)
                );
                setAiBuiltHtml(value);
                window.setTimeout(() => {
                    if (el) {
                        el.focus();
                        el.setSelectionRange(cursor, cursor);
                    }
                }, 0);
                caretSnapshotRef.current = null;
                return;
            }

            if (mode === "simple" && simpleEditorRef.current) {
                insertMergeTagInSimpleEditor(name, caret);
                caretSnapshotRef.current = null;
                return;
            }

            if (rawEditorRef.current?.insertText) {
                rawEditorRef.current.insertText(tag);
                caretSnapshotRef.current = null;
                return;
            }

            caretSnapshotRef.current = null;
        },
        [activeTab, mode, aiBuiltHtml, insertMergeTagInSimpleEditor]
    );

    const removeMergeVariable = useCallback(
        (name) => {
            if (!name) return;

            if (activeTab === "ai") {
                setAiBuiltHtml((prev) => removeEmailMergeParameterFromText(prev, name));
                return;
            }

            if (mode === "simple") {
                const plain = removeEmailMergeParameterFromText(
                    readPlainFromSimpleEditor(),
                    name
                );
                applyPlainHtmlToSimpleEditor(plain);
                return;
            }

            const plain = removeEmailMergeParameterFromText(
                readPlainFromRawEditor(),
                name
            );
            applyPlainHtmlToRawEditor(plain, { format: false });
        },
        [
            activeTab,
            mode,
            readPlainFromSimpleEditor,
            applyPlainHtmlToSimpleEditor,
            readPlainFromRawEditor,
            applyPlainHtmlToRawEditor,
        ]
    );

    const renameMergeVariable = useCallback(
        (oldName, newName) => {
            if (!oldName || !newName || oldName === newName) return;

            const nextSimple = renameEmailMergeParameterInText(
                contentByMode.simple,
                oldName,
                newName
            );
            const nextRaw = renameEmailMergeParameterInText(contentByMode.raw, oldName, newName);
            const nextAi = renameEmailMergeParameterInText(aiBuiltHtml, oldName, newName);

            setContentByMode({ simple: nextSimple, raw: nextRaw });
            setAiBuiltHtml(nextAi);

            if (activeTab === "simple" && mode === "simple") {
                applyPlainHtmlToSimpleEditor(nextSimple);
            } else if (mode === "raw") {
                applyPlainHtmlToRawEditor(nextRaw, { format: false });
            }
        },
        [
            contentByMode.simple,
            contentByMode.raw,
            aiBuiltHtml,
            activeTab,
            mode,
            applyPlainHtmlToSimpleEditor,
            applyPlainHtmlToRawEditor,
        ]
    );

    const canProceedFromEditor = Boolean(
        (getActiveHtml().trim() || previewContent.trim()) && !mergeParameterError
    );
    const canProceedFromName = Boolean(templateName.trim());
    const typeLabel =
        activeTab === "ai" ? "AI Powered" : activeTab === "raw" ? "Raw HTML" : "Simple";

    const resetForm = useCallback(() => {
        setMode("simple");
        setActiveTab("simple");
        setContentByMode(EMPTY_CONTENT);
        setAiPrompt("");
        setAiContext([]);
        setAiBuiltHtml("");
        setAiViewMode("preview");
        setAiIsBuilding(false);
        setAiError("");
        setEditingTemplateId(null);
        setTemplateName("");
        setWizardStep(0);
        setEditorWidthPercent(50);
        if (simpleEditorRef.current) {
            simpleEditorRef.current.innerHTML = "";
        }
        rawEditorRef.current?.setValue?.("", { format: false });
    }, []);

    useEffect(() => {
        resetForm();

        const editId = searchParams.get("editId");
        if (!editId) return;

        const fromState = location.state?.templateData;
        const fromStore = getDummyTemplateById(editId);
        const existing =
            fromState && String(fromState.id) === String(editId)
                ? fromState
                : fromStore;

        if (!existing) return;

        const typeTab =
            existing.type === "Raw HTML" ? "raw" : existing.type === "AI Powered" ? "ai" : "simple";
        const html = normalizeEmailHtmlForEditor(existing.html || "");
        const rawHtml = typeTab === "raw" ? formatEmailHtmlSource(html) : "";

        setEditingTemplateId(existing.id);
        setTemplateName(existing.name || "");
        setActiveTab(typeTab);
        setMode(typeTab === "raw" ? "raw" : "simple");
        setEditorWidthPercent(typeTab === "ai" ? 75 : 50);
        setContentByMode({
            simple: typeTab === "simple" ? html : "",
            raw: rawHtml,
        });
        setAiBuiltHtml(typeTab === "ai" ? html : "");

        window.setTimeout(() => {
            if (typeTab === "simple" && simpleEditorRef.current) {
                applyPlainHtmlToSimpleEditor(html);
            }
            if (typeTab === "raw") {
                applyPlainHtmlToRawEditor(rawHtml, { format: false });
            }
        }, 0);
    }, [
        location.pathname,
        location.state,
        searchParams,
        resetForm,
        applyPlainHtmlToSimpleEditor,
        applyPlainHtmlToRawEditor,
    ]);

    const startResize = (event) => {
        event.preventDefault();
        isDraggingRef.current = true;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    };

    useEffect(() => {
        const handleMouseMove = (event) => {
            if (!isDraggingRef.current || !splitContainerRef.current) return;

            const rect = splitContainerRef.current.getBoundingClientRect();
            const nextPercent = ((event.clientX - rect.left) / rect.width) * 100;
            setEditorWidthPercent(
                Math.min(MAX_EDITOR_WIDTH, Math.max(MIN_EDITOR_WIDTH, nextPercent))
            );
        };

        const handleMouseUp = () => {
            if (!isDraggingRef.current) return;
            isDraggingRef.current = false;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    const handleModeChange = (nextMode) => {
        const currentHtml = normalizePlainEmailHtml(getActiveHtml());

        if (nextMode === "ai") {
            if (currentHtml.trim()) {
                setAiBuiltHtml(currentHtml);
            }
            setContentByMode({ simple: currentHtml, raw: currentHtml });
            setActiveTab("ai");
            setMode("simple");
            setEditorWidthPercent(75);
            return;
        }

        const sourceHtml =
            activeTab === "ai"
                ? normalizePlainEmailHtml(aiBuiltHtml)
                : currentHtml;
        const rawHtml =
            nextMode === "raw" ? formatEmailHtmlSource(sourceHtml) : sourceHtml;

        setContentByMode({ simple: sourceHtml, raw: rawHtml });
        setActiveTab(nextMode);
        setMode(nextMode);
        setEditorWidthPercent(50);

        window.setTimeout(() => {
            if (nextMode === "simple") {
                applyPlainHtmlToSimpleEditor(sourceHtml);
            } else if (nextMode === "raw") {
                applyPlainHtmlToRawEditor(rawHtml, { format: false });
            }
        }, 0);
    };

    const handleSimplePaste = (event) => {
        handleSimpleEditorPaste(event, refreshSimpleEditorDecoration);
    };

    const handleRawHtmlChange = useCallback(
        (next) => {
            setRawHtml(next);
        },
        [setRawHtml]
    );

    const preserveToolbarSelection = (event) => {
        // Keep contentEditable selection when clicking toolbar controls.
        event.preventDefault();
    };

    const runEditorCommand = (command, value = null) => {
        runSimpleEditorCommand(command, value);
    };

    const handleCreateLink = () => {
        const editor = simpleEditorRef.current;
        if (!editor) return;

        const selection = window.getSelection();
        const range =
            selection && selection.rangeCount > 0
                ? selection.getRangeAt(0).cloneRange()
                : null;

        const url = window.prompt("Enter link URL");
        if (!url) return;

        editor.focus();
        if (range && selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }
        runSimpleEditorCommand("createLink", url);
    };

    const syncEditorContent = useCallback(() => {
        const html = getActiveHtml();
        if (activeTab === "ai") {
            setAiBuiltHtml(html);
            return html;
        }
        if (mode === "simple") {
            setContentByMode((prev) => ({ ...prev, simple: html }));
        } else {
            setContentByMode((prev) => ({ ...prev, raw: html }));
        }
        return html;
    }, [activeTab, mode, getActiveHtml]);

    const handleNext = () => {
        if (wizardStep === 0) {
            const html = syncEditorContent().trim();
            if (!html) return;
            const paramError = validateEmailMergeParameters(html);
            if (paramError) {
                notify(paramError, "error");
                return;
            }
            setWizardStep(1);
            return;
        }
        if (wizardStep === 1) {
            if (!templateName.trim()) return;
            setWizardStep(2);
        }
    };

    const handleBack = () => {
        if (wizardStep > 0) {
            setWizardStep((prev) => prev - 1);
        }
    };

    const handleCancel = () => {
        resetForm();
        navigate("/email/templateList");
    };

    const handleSave = async () => {
        const htmlContent =
            ((activeTab === "ai" ? aiBuiltHtml : getActiveHtml()).trim() || previewContent.trim());
        const title = templateName.trim();

        if (!htmlContent || !title) {
            notify("Some fields are missing.", "error");
            return;
        }

        const paramError = validateEmailMergeParameters(htmlContent);
        if (paramError) {
            notify(paramError, "error");
            return;
        }

        const payload = {
            user_id: userId,
            role: userRole,
            title,
            type: "0",
            template: htmlContent,
            template_design: null,
            ...buildTemplateApiParamFields(htmlContent),
        };

        setIsSaving(true);
        try {
            if (editingTemplateId) {
                await axios.post(base_url + "/email-template/update", {
                    template_id: editingTemplateId,
                    ...payload,
                });
                notify("Template updated successfully", "success");
            } else {
                await axios.post(base_url + "/email-template/create", payload);
                notify("Template created successfully", "success");
            }

            navigate("/email/templateList");
        } catch (error) {
            console.error("Error saving template:", error);
            notify("Failed to save template", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handlePreviewInNewTab = () => {
        const html = previewContent.trim();
        if (!html) return;

        const fullHtml = buildEmailPreviewDocumentHtml(html);
        const previewWindow = window.open("", "_blank");
        if (!previewWindow) return;

        previewWindow.document.open();
        previewWindow.document.write(fullHtml);
        previewWindow.document.close();
    };

    const handleAiPromptSubmit = async () => {
        const prompt = aiPrompt.trim();
        if (!prompt || aiIsBuilding) return;

        const userMessage = { id: Date.now(), role: "user", text: prompt };
        const nextContext = [...aiContext, userMessage];

        setAiContext(nextContext);
        setAiPrompt("");
        setAiIsBuilding(true);
        setAiError("");

        try {
            const result = await generateEmailTemplateWithGemini({
                prompt,
                currentHtml: aiBuiltHtml,
                context: nextContext,
            });

            setAiBuiltHtml(result.html);
            setAiContext((prev) => [
                ...prev,
                {
                    id: Date.now() + 1,
                    role: "assistant",
                    text: result.summary,
                },
            ]);
        } catch (error) {
            const message =
                error?.response?.data?.error?.message ||
                error?.message ||
                "Failed to generate template with Gemini.";

            setAiError(message);
            setAiContext((prev) => [
                ...prev,
                {
                    id: Date.now() + 1,
                    role: "assistant",
                    text: message,
                },
            ]);
        } finally {
            setAiIsBuilding(false);
        }
    };

    return (
        <Box
            sx={{
                ...WB_SX.page,
                p: 0,
                height: "calc(100vh - 96px)",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            <Box sx={{ width: "100%", maxWidth: "100%", mb: 2, minWidth: 0, flexShrink: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: { xs: "flex-start", md: "center" },
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <Box sx={{ maxWidth: { xs: "100%", md: "760px" } }}>
                        <Typography
                            variant="h5"
                            sx={{
                                ...WB_UI.typography.pageTitle,
                                color: "text.primary",
                                fontWeight: 800,
                            }}
                        >
                            {editingTemplateId ? "Edit Email Template" : "Create New Template"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                            {wizardStep === 0
                                ? "Design your email using Simple Editor, Raw HTML, or AI Powered tools."
                                : wizardStep === 1
                                  ? "Give your template a name so you can find it later."
                                  : "Review your template before saving."}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Close fontSize="small" />}
                            onClick={handleCancel}
                            sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: 2,
                                "&:hover": {
                                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        {wizardStep > 0 && (
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBack fontSize="small" />}
                                onClick={handleBack}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    borderRadius: 2,
                                }}
                            >
                                Back
                            </Button>
                        )}
                        {wizardStep < 2 ? (
                            <Button
                                variant="contained"
                                endIcon={<ArrowForward fontSize="small" />}
                                onClick={handleNext}
                                disabled={wizardStep === 0 ? !canProceedFromEditor : !canProceedFromName}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    borderRadius: 2,
                                }}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon fontSize="small" />}
                                onClick={handleSave}
                                disabled={
                                    isSaving ||
                                    !previewContent.trim() ||
                                    !templateName.trim()
                                }
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    borderRadius: 2,
                                }}
                            >
                                {isSaving ? "Saving..." : "Save"}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box sx={{ width: "100%", maxWidth: "100%", mb: 2, minWidth: 0, overflowX: "hidden", flexShrink: 0 }}>
                <CustomizedSteppers currentStep={wizardStep} steps={WIZARD_STEPS} />
            </Box>

            {wizardStep === 0 && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                        mb: 2,
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                        {[
                            { key: "simple", label: "Simple Editor", icon: <AutoAwesome sx={{ fontSize: 16 }} /> },
                            { key: "raw", label: "Raw HTML", icon: <Code sx={{ fontSize: 16 }} /> },
                            { key: "ai", label: "AI Powered", icon: <AutoAwesome sx={{ fontSize: 16 }} /> },
                        ].map((tab) => {
                            const active = activeTab === tab.key;
                            return (
                                <Button
                                    key={tab.key}
                                    startIcon={tab.icon}
                                    onClick={() => handleModeChange(tab.key)}
                                    sx={{
                                        textTransform: "none",
                                        color: active ? "primary.main" : "text.secondary",
                                        fontWeight: active ? 700 : 500,
                                        borderBottom: active ? "2px solid" : "2px solid transparent",
                                        borderRadius: 0,
                                        px: 1.25,
                                        py: 1,
                                        minWidth: "auto",
                                    }}
                                >
                                    {tab.label}
                                </Button>
                            );
                        })}
                    </Box>
                    <EmailMergeVariablesBar
                        variables={detectedVariables}
                        onPrepareInsert={captureEditorCaret}
                        onInsertVariable={insertMergeVariable}
                        onRemoveVariable={removeMergeVariable}
                    />
                </Box>
            )}

            {wizardStep === 0 && mergeParameterError && (
                <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                    {mergeParameterError}
                </Alert>
            )}

            <Paper
                elevation={0}
                sx={{
                    ...GLASS_PAPER_SX,
                    p: 0,
                    overflow: "hidden",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {wizardStep === 1 ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: { xs: 2, md: 4 },
                            minHeight: 0,
                            overflow: "auto",
                        }}
                    >
                        <Box sx={{ width: "100%", maxWidth: 480 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                Template Name
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                                Give your email template a name so you can find it later.
                            </Typography>
                            <TextField
                                fullWidth
                                label="Template Name"
                                value={templateName}
                                onChange={(event) => setTemplateName(event.target.value)}
                                placeholder="e.g. Welcome Email"
                                autoFocus
                                sx={fieldSx}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" && templateName.trim()) {
                                        handleNext();
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                ) : wizardStep === 2 ? (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: "auto",
                            p: { xs: 2, md: 3 },
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                bgcolor: "#f5f7fb",
                                border: "1px solid rgba(0,0,0,0.08)",
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Template Name
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>
                                {templateName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                Type: {typeLabel}
                            </Typography>
                        </Paper>

                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Email Preview
                            </Typography>
                            <Tooltip title="Preview in new tab">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={handlePreviewInNewTab}
                                        disabled={!previewContent.trim()}
                                        sx={{ color: "text.secondary" }}
                                    >
                                        <OpenInNew fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                border: "1px solid rgba(0,0,0,0.08)",
                                borderRadius: 2,
                                p: 2,
                                bgcolor: "#fff",
                                minHeight: 280,
                                overflow: "auto",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                            }}
                            dangerouslySetInnerHTML={{ __html: highlightedPreviewHtml }}
                        />
                    </Box>
                ) : (
                <Box
                    ref={splitContainerRef}
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            p: activeTab === "ai" ? 0 : 2,
                            width: { xs: "100%", md: `${editorWidthPercent}%` },
                            minWidth: 0,
                            minHeight: 0,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            flexShrink: 0,
                            bgcolor: activeTab === "ai" ? "#fff" : "transparent",
                            borderRight: activeTab === "ai" ? "1px solid rgba(0,0,0,0.08)" : "none",
                        }}
                    >
                        {activeTab === "ai" ? (
                            <>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        px: 2,
                                        py: 1,
                                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                                        flexShrink: 0,
                                        bgcolor: "#fafafa",
                                    }}
                                >
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                        {[
                                            { key: "preview", label: "Preview" },
                                            { key: "code", label: "Code" },
                                        ].map((tab) => {
                                            const active = aiViewMode === tab.key;
                                            return (
                                                <Button
                                                    key={tab.key}
                                                    onClick={() => setAiViewMode(tab.key)}
                                                    sx={{
                                                        textTransform: "none",
                                                        minWidth: "auto",
                                                        px: 1.5,
                                                        py: 0.75,
                                                        fontWeight: active ? 700 : 500,
                                                        color: active ? "primary.main" : "text.secondary",
                                                        borderBottom: active ? "2px solid" : "2px solid transparent",
                                                        borderRadius: 0,
                                                    }}
                                                >
                                                    {tab.label}
                                                </Button>
                                            );
                                        })}
                                    </Box>

                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Tooltip title="Preview in new tab">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={handlePreviewInNewTab}
                                                    disabled={!aiBuiltHtml.trim()}
                                                    sx={{ color: "text.secondary" }}
                                                >
                                                    <OpenInNew fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        overflow: "auto",
                                        p: 2,
                                        bgcolor: "#f5f7fb",
                                    }}
                                >
                                    {aiIsBuilding ? (
                                        <AiPreviewCraftingLoader />
                                    ) : aiViewMode === "preview" ? (
                                        aiBuiltHtml ? (
                                            <Box
                                                sx={{
                                                    minHeight: "100%",
                                                    border: "1px solid rgba(0,0,0,0.08)",
                                                    borderRadius: 1.5,
                                                    bgcolor: "#fff",
                                                    p: 2,
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html: decorateEmailMergeTagsForPreview(
                                                        aiBuiltHtml
                                                    ),
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    minHeight: "100%",
                                                    border: "1px dashed rgba(0,0,0,0.15)",
                                                    borderRadius: 1.5,
                                                    bgcolor: "#fff",
                                                    p: 3,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "text.secondary",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <Typography variant="body2">
                                                    Your AI-generated email preview will appear here.
                                                </Typography>
                                            </Box>
                                        )
                                    ) : (
                                        <TextField
                                            fullWidth
                                            multiline
                                            inputRef={aiTextareaRef}
                                            value={aiBuiltHtml}
                                            onChange={(event) => setAiBuiltHtml(event.target.value)}
                                            sx={{
                                                ...fieldSx,
                                                minHeight: "100%",
                                                "& .MuiInputBase-root": {
                                                    height: "100%",
                                                    alignItems: "flex-start",
                                                },
                                                "& .MuiInputBase-inputMultiline": {
                                                    minHeight: "100% !important",
                                                    overflow: "auto !important",
                                                    fontFamily: "Consolas, monospace",
                                                    fontSize: "0.82rem",
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                            </>
                        ) : mode === "simple" ? (
                            <Box
                                sx={{
                                    border: "1px solid rgba(0,0,0,0.12)",
                                    borderRadius: 2,
                                    backgroundColor: "#fff",
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: 1,
                                    minHeight: 0,
                                }}
                            >
                                <Box
                                    onMouseDownCapture={preserveToolbarSelection}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        flexWrap: "wrap",
                                        px: 1.5,
                                        py: 1.25,
                                        borderBottom: "1px solid rgba(0,0,0,0.12)",
                                        backgroundColor: "#fff",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Button size="small" onClick={() => runEditorCommand("bold")} sx={{ minWidth: "auto", color: "text.primary", fontWeight: 800 }}><FormatBold fontSize="small" /></Button>
                                    <Button size="small" onClick={() => runEditorCommand("italic")} sx={{ minWidth: "auto", color: "text.primary" }}><FormatItalic fontSize="small" /></Button>
                                    <Button size="small" onClick={() => runEditorCommand("underline")} sx={{ minWidth: "auto", color: "text.primary" }}><FormatUnderlined fontSize="small" /></Button>
                                    <Button size="small" onClick={() => runEditorCommand("strikeThrough")} sx={{ minWidth: "auto", color: "text.primary", fontWeight: 700 }}>S</Button>
                                    <Divider orientation="vertical" flexItem />
                                    <Button size="small" onClick={() => runEditorCommand("insertUnorderedList")} sx={{ minWidth: "auto", color: "text.primary" }}><FormatListBulleted fontSize="small" /></Button>
                                    <Button size="small" onClick={() => runEditorCommand("insertOrderedList")} sx={{ minWidth: "auto", color: "text.primary" }}><FormatListNumbered fontSize="small" /></Button>
                                    <Button size="small" onClick={handleCreateLink} sx={{ minWidth: "auto", color: "primary.main" }}><LinkIcon fontSize="small" /></Button>
                                    <Button size="small" sx={{ minWidth: "auto", color: "primary.main" }}><ImageIcon fontSize="small" /></Button>
                                    <Button size="small" sx={{ minWidth: "auto", color: "text.primary" }}><CropLandscape fontSize="small" /></Button>
                                </Box>

                                <Box
                                    key="simple-email-editor"
                                    ref={simpleEditorRef}
                                    contentEditable
                                    suppressContentEditableWarning
                                    onInput={handleSimpleInput}
                                    onBlur={handleSimpleBlur}
                                    onMouseDown={handleSimpleEditorMouseDown}
                                    onClick={handleSimpleEditorClick}
                                    onCopy={handleSimpleEditorCopy}
                                    onCut={handleSimpleEditorCut}
                                    onContextMenu={handleSimpleEditorContextMenu}
                                    onPaste={handleSimplePaste}
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        overflow: "auto",
                                        p: 2,
                                        outline: "none",
                                        whiteSpace: "normal",
                                        fontSize: "0.92rem",
                                        lineHeight: 1.55,
                                        "& p": { my: 0.5 },
                                        "& h1": { fontSize: "1.5rem", my: 1 },
                                        "& h2": { fontSize: "1.2rem", my: 1 },
                                        "& ul, & ol": { pl: 3, my: 1 },
                                        "& a": { color: "primary.main" },
                                        ...EMAIL_MERGE_TAG_EDITOR_SX,
                                    }}
                                />
                            </Box>
                        ) : (
                            <RawHtmlCodeEditor
                                key="raw-email-editor"
                                ref={rawEditorRef}
                                value={contentByMode.raw}
                                onChange={handleRawHtmlChange}
                                onFocusCaret={captureEditorCaret}
                                placeholder="Paste your HTML content here"
                            />
                        )}
                    </Box>

                    <Box
                        onMouseDown={startResize}
                        sx={{
                            display: { xs: "none", md: "flex" },
                            width: SPLITTER_WIDTH,
                            flexShrink: 0,
                            cursor: "col-resize",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(0,0,0,0.04)",
                            borderLeft: "1px solid rgba(0,0,0,0.08)",
                            borderRight: "1px solid rgba(0,0,0,0.08)",
                            transition: "background-color 0.15s ease",
                            "&:hover": {
                                bgcolor: "rgba(25,118,210,0.18)",
                            },
                            "&::after": {
                                content: '""',
                                width: 2,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: "rgba(0,0,0,0.18)",
                            },
                        }}
                    />

                    <Box
                        sx={{
                            p: activeTab === "ai" ? 0 : 2,
                            flex: 1,
                            minWidth: 0,
                            backgroundColor: activeTab === "ai" ? "#fff" : "#fff",
                            minHeight: 0,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {activeTab === "ai" ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        overflow: "auto",
                                        px: 2,
                                        py: 1.5,
                                    }}
                                >
                                    {aiContext.length === 0 && !aiIsBuilding && (
                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            Start with a prompt like: Create a welcome email with a blue header and CTA button.
                                        </Typography>
                                    )}
                                    {aiContext.map((item) => (
                                        <Box
                                            key={item.id}
                                            sx={{
                                                mb: 1.5,
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                bgcolor: "#fff",
                                                border: "1px solid rgba(0,0,0,0.08)",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                        item.role === "user"
                                                            ? "primary.main"
                                                            : item.role === "assistant"
                                                              ? item.text.toLowerCase().includes("error") ||
                                                                item.text.toLowerCase().includes("failed")
                                                                  ? "error.main"
                                                                  : "success.main"
                                                              : "text.secondary",
                                                    fontWeight: 700,
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.4,
                                                }}
                                            >
                                                {item.role}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.75, color: "text.primary" }}>
                                                {item.text}
                                            </Typography>
                                        </Box>
                                    ))}
                                    {aiIsBuilding && <AiChatCraftingCard />}
                                </Box>

                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        borderTop: "1px solid rgba(0,0,0,0.08)",
                                        bgcolor: "#fff",
                                        p: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            border: "1px solid rgba(0,0,0,0.12)",
                                            borderRadius: 2,
                                            p: 1.25,
                                            bgcolor: aiIsBuilding ? "#f0f0f0" : "#fafafa",
                                            opacity: aiIsBuilding ? 0.72 : 1,
                                            transition: "opacity 0.2s ease, background-color 0.2s ease",
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            maxRows={6}
                                            placeholder={
                                                aiIsBuilding
                                                    ? "Crafting your email… please wait"
                                                    : "Make changes, add new features, ask for anything"
                                            }
                                            value={aiPrompt}
                                            disabled={aiIsBuilding}
                                            onChange={(event) => setAiPrompt(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (aiIsBuilding) {
                                                    event.preventDefault();
                                                    return;
                                                }
                                                if (event.key === "Enter" && !event.shiftKey) {
                                                    event.preventDefault();
                                                    handleAiPromptSubmit();
                                                }
                                            }}
                                            variant="standard"
                                            InputProps={{ disableUnderline: true }}
                                            sx={{
                                                "& .MuiInputBase-input": {
                                                    fontSize: "0.9rem",
                                                    lineHeight: 1.5,
                                                },
                                                "& .MuiInputBase-input.Mui-disabled": {
                                                    WebkitTextFillColor: "rgba(0,0,0,0.45)",
                                                },
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                                mt: 1,
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={handleAiPromptSubmit}
                                                disabled={!aiPrompt.trim() || aiIsBuilding}
                                                sx={{
                                                    bgcolor: "primary.main",
                                                    color: "#fff",
                                                    "&:hover": { bgcolor: "primary.dark" },
                                                    "&.Mui-disabled": {
                                                        bgcolor: "action.disabledBackground",
                                                        color: "action.disabled",
                                                    },
                                                }}
                                            >
                                                <ArrowUpward fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    {aiError && (
                                        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                                            {aiError}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: 1,
                                        flexShrink: 0,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "text.secondary", fontWeight: 600 }}
                                    >
                                        Live Preview
                                    </Typography>
                                    <Tooltip title="Preview in new tab">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={handlePreviewInNewTab}
                                                disabled={!previewContent.trim()}
                                                sx={{ color: "text.secondary" }}
                                            >
                                                <OpenInNew fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                {mode === "raw" || mode === "simple" ? (
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: "auto",
                                            border: "1px solid rgba(0,0,0,0.08)",
                                            borderRadius: 1,
                                            p: 2,
                                            backgroundColor: "#fff",
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                        dangerouslySetInnerHTML={{ __html: highlightedPreviewHtml }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: "auto",
                                            border: "1px solid rgba(0,0,0,0.08)",
                                            borderRadius: 1,
                                            p: 2,
                                            whiteSpace: "pre-wrap",
                                            color: "text.primary",
                                            fontFamily: "Consolas, monospace",
                                            fontSize: "0.82rem",
                                            backgroundColor: "#fff",
                                        }}
                                    >
                                        {content || "Preview will appear here."}
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
                )}

            </Paper>

            <EmailMergeParameterNameDialog
                open={Boolean(renameParameter)}
                mode="rename"
                initialName={renameParameter || ""}
                excludeName={renameParameter}
                usedNames={detectedVariables}
                onClose={() => setRenameParameter(null)}
                onConfirm={(newName) => {
                    renameMergeVariable(renameParameter, newName);
                    setRenameParameter(null);
                }}
            />
        </Box>
    );
};

export default CreateNewTemplate;
